import net from "node:net";
import { mkdir, writeFile } from "node:fs/promises";
import { dirname } from "node:path";

/**
 * Sidecar readiness gate.
 *
 * Previously the pod entrypoint blocked on postgres/firebase coming up BEFORE it
 * launched the agent, so the agent's thinking loop paid for up to ~60s of
 * sidecar boot it never needed. That wait now lives here and runs inside the
 * agent's background setup IIFE — it gates only `setupCommand`/`startCommand`
 * (which genuinely need a warm postgres), never the agent loop.
 *
 * Semantics deliberately match the old entrypoint: best-effort, NON-FATAL on
 * timeout (log a warning and continue), and a no-op when no sidecar env vars are
 * present. Per-target timeouts mirror what the entrypoint learned the hard way —
 * the firebase auth emulator cold-boots slowly and doesn't bind 9099 until ~50s
 * after its container starts, so it gets 60s.
 *
 * Postgres gets 120s: on a durable-sleep/pool pod the postgres sidecar restores
 * prior state, and an ungraceful previous pod death (e.g. OOMKill) leaves a torn
 * WAL that triggers crash recovery on boot. Cold boot + WAL replay routinely runs
 * past 30s, so the old deadline expired before postgres bound 5432 — setup's seed
 * then hit `DatabaseNotReachable` (P1001) and the whole dev env came up broken.
 */

export interface SidecarTarget {
  /** Human label for logs (e.g. "postgres"). */
  name: string;
  host: string;
  port: number;
  /** Per-target readiness deadline in ms (overridden by an explicit opts.timeoutMs). */
  timeoutMs?: number;
}

export interface WaitForSidecarsOptions {
  /** Env to read targets from. Defaults to `process.env`. */
  env?: NodeJS.ProcessEnv;
  /** Progress sink (one line per message, no trailing newline). */
  onLog?: (message: string) => void;
  /** Force a single deadline for every target, overriding per-target defaults. */
  timeoutMs?: number;
  /** Delay between probe attempts in ms. */
  pollIntervalMs?: number;
  /** TCP probe seam — returns true once the target accepts a connection. */
  probe?: (target: SidecarTarget) => Promise<boolean>;
  /** Whether to create the lazy sidecar start marker before probing. Defaults to true. */
  startLazy?: boolean;
}

const POSTGRES_TIMEOUT_MS = 120_000;
const FIREBASE_TIMEOUT_MS = 60_000;
const FALLBACK_TIMEOUT_MS = 30_000;
const DEFAULT_POLL_INTERVAL_MS = 1_000;
const DEFAULT_PROBE_TIMEOUT_MS = 2_000;

const POSTGRES_DEFAULT_PORT = 5432;
const FIREBASE_DEFAULT_PORT = 9099;

export async function startLazySidecars(
  env: NodeJS.ProcessEnv,
  onLog: (message: string) => void,
): Promise<void> {
  const markerPath = env.CONVEYOR_SIDECAR_START_FILE;
  if (!markerPath) return;

  try {
    await mkdir(dirname(markerPath), { recursive: true });
    await writeFile(markerPath, "start\n", "utf8");
    onLog("Started lazy sidecars");
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    onLog(`WARNING: failed to start lazy sidecars: ${message}`);
  }
}

function parseHostPort(value: string, defaultPort: number): { host: string; port: number } | null {
  const trimmed = value.trim().replace(/^[a-z]+:\/\//i, "");
  if (!trimmed) return null;
  const idx = trimmed.lastIndexOf(":");
  if (idx === -1) {
    return { host: trimmed, port: defaultPort };
  }
  const host = trimmed.slice(0, idx) || "localhost";
  const port = Number(trimmed.slice(idx + 1));
  return { host, port: Number.isFinite(port) ? port : defaultPort };
}

/**
 * Resolve the sidecar targets the agent should wait for from env. Mirrors the
 * entrypoint's conditions: postgres when `DATABASE_URL` is set, firebase auth
 * emulator when `FIREBASE_AUTH_EMULATOR_HOST` is set.
 */
export function resolveSidecarTargets(env: NodeJS.ProcessEnv = process.env): SidecarTarget[] {
  const targets: SidecarTarget[] = [];

  const databaseUrl = env.DATABASE_URL;
  if (databaseUrl) {
    try {
      const url = new URL(databaseUrl);
      const host = url.hostname || "localhost";
      const port = url.port ? Number(url.port) : POSTGRES_DEFAULT_PORT;
      if (Number.isFinite(port)) {
        targets.push({ name: "postgres", host, port, timeoutMs: POSTGRES_TIMEOUT_MS });
      }
    } catch {
      // Unparseable DATABASE_URL — skip rather than crash the setup path.
    }
  }

  const firebaseHost = env.FIREBASE_AUTH_EMULATOR_HOST;
  if (firebaseHost) {
    const parsed = parseHostPort(firebaseHost, FIREBASE_DEFAULT_PORT);
    if (parsed) {
      targets.push({
        name: "firebase auth emulator",
        host: parsed.host,
        port: parsed.port,
        timeoutMs: FIREBASE_TIMEOUT_MS,
      });
    }
  }

  return targets;
}

function defaultProbe(target: SidecarTarget): Promise<boolean> {
  return new Promise((resolve) => {
    let settled = false;
    const socket = net.createConnection({ host: target.host, port: target.port });
    const done = (ok: boolean) => {
      if (settled) return;
      settled = true;
      socket.destroy();
      resolve(ok);
    };
    socket.once("connect", () => done(true));
    socket.once("error", () => done(false));
    socket.setTimeout(DEFAULT_PROBE_TIMEOUT_MS, () => done(false));
  });
}

const delay = (ms: number): Promise<void> =>
  new Promise((resolve) => {
    setTimeout(resolve, ms);
  });

async function waitForTarget(
  target: SidecarTarget,
  opts: {
    onLog: (message: string) => void;
    timeoutMs?: number;
    pollIntervalMs: number;
    probe: (target: SidecarTarget) => Promise<boolean>;
  },
): Promise<void> {
  const { onLog, pollIntervalMs, probe } = opts;
  const timeoutMs = opts.timeoutMs ?? target.timeoutMs ?? FALLBACK_TIMEOUT_MS;
  const deadline = Date.now() + timeoutMs;
  onLog(`Waiting for ${target.name} on ${target.host}:${target.port}...`);

  while (true) {
    if (await probe(target)) {
      onLog(`${target.name} is ready`);
      return;
    }
    if (Date.now() >= deadline) {
      onLog(
        `WARNING: ${target.name} not ready after ${Math.round(timeoutMs / 1000)}s, continuing anyway`,
      );
      return;
    }
    await delay(pollIntervalMs);
  }
}

/**
 * Block until every configured sidecar is reachable (or its per-target deadline
 * elapses). Resolves immediately when no sidecar env vars are set. Never throws —
 * a timed-out sidecar logs a warning and is treated as "continue anyway".
 */
export async function waitForSidecars(opts: WaitForSidecarsOptions = {}): Promise<void> {
  const {
    env = process.env,
    onLog = () => {},
    timeoutMs,
    pollIntervalMs = DEFAULT_POLL_INTERVAL_MS,
    probe = defaultProbe,
    startLazy = true,
  } = opts;

  if (startLazy) {
    await startLazySidecars(env, onLog);
  }

  const targets = resolveSidecarTargets(env);
  if (targets.length === 0) return;

  await Promise.all(
    targets.map((target) => waitForTarget(target, { onLog, timeoutMs, pollIntervalMs, probe })),
  );
}
